import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, forkJoin, of, Subject, takeUntil } from 'rxjs';
import { FieldMappingComponent } from '../../common/field-mapping/field-mapping.component';
import { FileUploadComponent } from '../../common/file-upload/file-upload.component';
import { UploadFile } from '../../../core/models/upload-file-fields';
import { RestService } from '../../../core/services/rest.service';

// ── Public types ──────────────────────────────────────────────────────────────

export type ImportSource = 'zephyr' | 'xlsx';

export interface SystemField {
  fieldKey: string;
  label: string;
  required: boolean;
}

export interface ExtractedColumn {
  source: string;
  field: string;
}

/** Close result that tells the caller to replace this dialog with the Zephyr modal. */
export interface OpenZephyrImportResult {
  openZephyr: true;
  connectionId: string;
  connectionName: string;
}

export interface ImportTestCasesResult {
  source: ImportSource;
  documentType: string;
  connectionId: string;
  connectionName: string;
  /** fieldKey → { source, field } mapping sent to save_mapping. */
  fieldMappings: Record<string, ExtractedColumn>;
  /** Test cases returned by save_mapping — ready for the TCG list. */
  extractedTestCases: any[];
}

// ── Internal types ────────────────────────────────────────────────────────────

interface DocumentType {
  value: string;
  label: string;
  extensions: string[];
}

type ModalStep = 1 | 2;

@Component({
  selector: 'app-import-test-cases-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, FileUploadComponent, FieldMappingComponent],
  templateUrl: './import-test-cases-modal.component.html',
  styleUrl: './import-test-cases-modal.component.scss',
})
export class ImportTestCasesModalComponent implements OnInit, OnDestroy {
  readonly activeModal = inject(NgbActiveModal);
  private readonly restService = inject(RestService);

  @Input() projectId = '';
  @Input() initialAlmConnectionId = '';

  // ── Import source options ─────────────────────────────────────────────────

  readonly importSources = [
    {
      key: 'zephyr',
      title: 'From Zephyr Essential',
      description: 'Import your existing Zephyr Essential test cases into your project.',
      disabled: false,
    },
    {
      key: 'xlsx',
      title: 'From XLSX / CSV',
      description: 'Import your existing test cases directly from an XLSX or CSV file.',
      disabled: false,
    },
  ] as const;

  readonly documentTypes: DocumentType[] = [
    { value: 'xlsx', label: 'XLSX', extensions: ['.xlsx'] },
    { value: 'csv',  label: 'CSV',  extensions: ['.csv'] },
  ];

  // ── Close button hover state (mirrors other modal components) ────────────
  isActive = false;

  // ── Step 1 state ──────────────────────────────────────────────────────────

  currentStep: ModalStep = 1;
  selectedSource: ImportSource = 'zephyr';
  selectedDocumentType = 'xlsx';
  selectedConnectionId = '';

  fileConnections: any[] = [];
  almConnections: any[] = [];
  selectedAlmProvider = '';
  selectedAlmConnectionId = '';
  isLoadingConnections = false;
  connectionsError = false;

  // ── Step 1 – Add-document inline form ────────────────────────────────────

  showAddDocForm = false;
  fileUploadKey = 0;
  addDocType = 'xlsx';
  addDocFile: File | null = null;
  isUploadingDoc = false;
  uploadError = '';

  // ── Step 2 state ──────────────────────────────────────────────────────────

  systemFields: SystemField[] = [];
  extractedColumns: ExtractedColumn[] = [];
  fieldMappings: Record<string, string> = {};
  isLoadingPreview = false;
  previewError = '';
  isImporting = false;
  importError = '';

  private readonly destroy$ = new Subject<void>();

  // ── Derived getters ───────────────────────────────────────────────────────

  get filteredConnections(): any[] {
    const type = this.documentTypes.find(t => t.value === this.selectedDocumentType);
    if (!type) return this.fileConnections;
    return this.fileConnections.filter((c: any) => {
      const name: string = (c.connectionName ?? '').toLowerCase();
      return type.extensions.some(ext => name.endsWith(ext));
    });
  }

  get selectedConnection(): any {
    return this.fileConnections.find(
      (c: any) => (c._id ?? c.id) === this.selectedConnectionId
    ) ?? null;
  }

  get addDocUploadConfig(): UploadFile {
    const type = this.documentTypes.find(t => t.value === this.addDocType);
    const exts = (type?.extensions ?? []).map(e => e.toUpperCase()).join(' or ');
    return {
      fileUploadLabel: '',
      allowMultiple: false,
      fileTypes: (type?.extensions ?? []).join(','),
      placeholderText: `${exts} file`,
    };
  }

  get addDocHintText(): string {
    const type = this.documentTypes.find(t => t.value === this.addDocType);
    return (type?.extensions ?? []).map(e => e.toUpperCase()).join(' or ') + ' file';
  }

  get isZephyrSource(): boolean {
    return this.selectedSource === 'zephyr';
  }

  get almProviders(): string[] {
    const providers = this.almConnections
      .map((connection) => String(connection?.meta?.source ?? '').trim())
      .filter((provider) => !!provider);
    return [...new Set(providers)];
  }

  get almConnectionsForProvider(): any[] {
    return this.almConnections.filter(
      (connection) => String(connection?.meta?.source ?? '') === this.selectedAlmProvider
    );
  }

  get canContinue(): boolean {
    if (this.isZephyrSource) {
      return !!this.selectedAlmConnectionId;
    }
    return !!this.selectedConnectionId;
  }

  get step2Title(): string {
    return 'Field Mapping';
  }

  get extractedColumnNames(): string[] {
    return this.extractedColumns.map((column) => column.field);
  }

  get requiredFieldsMapped(): boolean {
    return this.systemFields
      .filter(f => f.required)
      .every(f => !!this.fieldMappings[f.fieldKey]);
  }

  /** Only pass non-empty mappings, using source and field from preview-columns. */
  get activeColumnMappings(): Record<string, ExtractedColumn> {
    const mappings: Record<string, ExtractedColumn> = {};
    for (const [fieldKey, fieldName] of Object.entries(this.fieldMappings)) {
      const field = fieldName?.trim();
      if (!field) {
        continue;
      }
      const column = this.extractedColumns.find((col) => col.field === field);
      mappings[fieldKey] = {
        source: column?.source ?? 'FILE',
        field,
      };
    }
    return mappings;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadConnections();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Step 1 handlers ───────────────────────────────────────────────────────

  onDocumentTypeChange(): void {
    this.selectedConnectionId = '';
  }

  onAlmProviderChange(): void {
    const matches = this.almConnectionsForProvider;
    const stillValid = matches.some(
      (connection) => this.connectionRecordId(connection) === this.selectedAlmConnectionId
    );
    if (stillValid) {
      return;
    }
    this.selectedAlmConnectionId = matches.length === 1
      ? this.connectionRecordId(matches[0])
      : '';
  }

  connectionRecordId(connection: any): string {
    const id = connection?.id ?? connection?._id ?? '';
    return id ? String(id) : '';
  }

  onContinue(): void {
    if (!this.canContinue || this.isLoadingPreview) return;
    if (this.isZephyrSource) {
      this.openZephyrModal();
      return;
    }
    this.isLoadingPreview = true;
    this.previewError = '';

    // Call both endpoints in parallel:
    // 1. preview-columns  → system fields + extracted columns from the file
    // 2. import/mapping   → previously saved column mappings (may 404/empty)
    forkJoin({
      preview: this.restService.getImportPreviewColumns(
        this.projectId, this.selectedConnectionId
      ),
      saved: this.restService.getImportMapping(
        this.projectId, this.selectedConnectionId
      ).pipe(catchError(() => of(null))),   // treat 404 / errors as "no saved mapping"
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ preview, saved }) => {
          const inner = preview?.data?.data ?? preview?.data ?? {};
          this.systemFields = Array.isArray(inner.systemTestCasesFields)
            ? inner.systemTestCasesFields
            : [];
          this.extractedColumns = this.toExtractedColumns(inner.extractedColumns);

          const savedMappings: Record<string, unknown> | null =
            saved?.data?.columnMappings ?? null;

          if (savedMappings && Object.keys(savedMappings).length > 0) {
            // Pre-fill with saved mappings; only keep keys that still match a
            // known system field, and only use column values still present in
            // the extracted columns so stale mappings don't confuse the user.
            const extractedFields = new Set(this.extractedColumns.map((col) => col.field));
            this.fieldMappings = {};
            for (const field of this.systemFields) {
              const savedCol = this.savedColumnField(savedMappings[field.fieldKey]);
              if (savedCol && extractedFields.has(savedCol)) {
                this.fieldMappings[field.fieldKey] = savedCol;
              }
            }
          } else {
            // No saved mapping — fall back to label-based auto-map.
            this.autoMapFields();
          }

          this.isLoadingPreview = false;
          this.currentStep = 2;
        },
        error: (err: any) => {
          this.isLoadingPreview = false;
          this.previewError =
            err?.error?.message || err?.message || 'Failed to load columns. Please try again.';
        },
      });
  }

  // ── Step 2 handlers ───────────────────────────────────────────────────────

  onBackToStep1(): void {
    this.currentStep = 1;
    this.previewError = '';
  }

  onImport(): void {
    if (!this.requiredFieldsMapped || this.isImporting) return;
    this.isImporting = true;
    this.importError = '';

    this.restService
      .saveImportMapping(this.projectId, this.selectedConnectionId, this.activeColumnMappings)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isImporting = false;
          const inner = res?.data?.data ?? res?.data ?? {};
          const result: ImportTestCasesResult = {
            source: this.selectedSource,
            documentType: this.selectedDocumentType,
            connectionId: this.selectedConnectionId,
            connectionName: this.selectedConnection?.connectionName ?? '',
            fieldMappings: this.activeColumnMappings,
            extractedTestCases: inner.extractedTestCases ?? [],
          };
          this.activeModal.close(result);
        },
        error: (err: any) => {
          this.isImporting = false;
          this.importError =
            err?.error?.message || err?.message || 'Import failed. Please try again.';
        },
      });
  }

  // ── Add-document form handlers ────────────────────────────────────────────

  onAddDocument(): void {
    this.showAddDocForm = true;
    this.addDocType = this.selectedDocumentType;
    this.addDocFile = null;
    this.uploadError = '';
    this.fileUploadKey++;
  }

  onAddDocTypeChange(): void {
    this.addDocFile = null;
    this.uploadError = '';
    this.fileUploadKey++;
  }

  onFilesSelected(event: { files: File[] }): void {
    const file = event.files[0] ?? null;
    this.uploadError = '';
    if (!file) return;
    const type = this.documentTypes.find(t => t.value === this.addDocType);
    const valid = (type?.extensions ?? []).some(ext => file.name.toLowerCase().endsWith(ext));
    if (!valid) {
      this.uploadError = `Please select a valid ${this.addDocType.toUpperCase()} file (${this.addDocHintText}).`;
      this.fileUploadKey++;
      return;
    }
    this.addDocFile = file;
  }

  onConfirmAddDocument(): void {
    if (!this.addDocFile || this.isUploadingDoc) return;
    this.isUploadingDoc = true;
    this.uploadError = '';
    const uploadedName = this.addDocFile.name;
    this.restService
      .uploadNewTcgDocument(this.projectId, this.addDocFile, this.addDocType, uploadedName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isUploadingDoc = false;
          this.showAddDocForm = false;
          this.addDocFile = null;
          this.loadConnections(uploadedName);
        },
        error: (err: any) => {
          this.isUploadingDoc = false;
          this.uploadError = err?.error?.message || err?.message || 'Upload failed. Please try again.';
        },
      });
  }

  onCancelAddDocument(): void {
    this.showAddDocForm = false;
    this.addDocFile = null;
    this.uploadError = '';
  }

  // ── Modal actions ─────────────────────────────────────────────────────────

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private openZephyrModal(): void {
    const selected = this.almConnections.find(
      (connection) => this.connectionRecordId(connection) === this.selectedAlmConnectionId
    );
    const result: OpenZephyrImportResult = {
      openZephyr: true,
      connectionId: this.selectedAlmConnectionId,
      connectionName: selected?.connectionName ?? '',
    };
    this.activeModal.close(result);
  }

  private loadConnections(autoSelectName?: string): void {
    if (!this.projectId) return;
    this.isLoadingConnections = true;
    this.connectionsError = false;
    this.restService
      .getConnections(this.projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.fileConnections = res?.data?.FILE ?? [];
          this.almConnections = res?.data?.ALM ?? [];
          this.isLoadingConnections = false;
          this.applyInitialAlmConnection();

          if (autoSelectName) {
            const uploaded = this.fileConnections.find(
              (c: any) => (c.connectionName ?? '').toLowerCase() === autoSelectName.toLowerCase()
            );
            if (uploaded) {
              this.selectedConnectionId = uploaded._id ?? uploaded.id ?? '';
            }
          }
        },
        error: () => {
          this.isLoadingConnections = false;
          this.connectionsError = true;
        },
      });
  }

  private applyInitialAlmConnection(): void {
    const saved = this.almConnections.find(
      (connection) => this.connectionRecordId(connection) === this.initialAlmConnectionId
    );
    if (saved) {
      this.selectedAlmProvider = String(saved?.meta?.source ?? '');
      this.selectedAlmConnectionId = this.initialAlmConnectionId;
      return;
    }
    this.selectedAlmProvider = this.almProviders[0] ?? '';
    this.onAlmProviderChange();
  }

  /** Auto-map system fields to extracted columns by matching label (case-insensitive). */
  private autoMapFields(): void {
    this.fieldMappings = {};
    for (const field of this.systemFields) {
      const match = this.extractedColumns.find(
        (col) => col.field.trim().toLowerCase() === field.label.trim().toLowerCase()
      );
      if (match) {
        this.fieldMappings[field.fieldKey] = match.field;
      }
    }
  }

  /** Saved mappings may be a column name or the { source, field } object. */
  private savedColumnField(value: unknown): string {
    if (typeof value === 'string') {
      return value.trim();
    }
    if (value && typeof value === 'object' && 'field' in value) {
      const field = (value as { field?: unknown }).field;
      return typeof field === 'string' ? field.trim() : '';
    }
    return '';
  }

  /**
   * preview-columns returns extractedColumns as { source, field }.
   * Older responses used a plain string list. Both become the same shape.
   */
  private toExtractedColumns(raw: unknown): ExtractedColumn[] {
    if (!Array.isArray(raw)) {
      return [];
    }

    const columns: ExtractedColumn[] = [];
    for (const item of raw) {
      if (typeof item === 'string') {
        const field = item.trim();
        if (field) {
          columns.push({ source: 'FILE', field });
        }
        continue;
      }
      if (!item || typeof item !== 'object') {
        continue;
      }
      const record = item as { source?: unknown; field?: unknown };
      if (typeof record.field !== 'string') {
        continue;
      }
      const field = record.field.trim();
      if (!field) {
        continue;
      }
      const source = typeof record.source === 'string' && record.source.trim()
        ? record.source
        : 'FILE';
      columns.push({ source, field });
    }
    return columns;
  }
}
